<script lang="ts">
  import { get, writable } from "svelte/store";
  import { onMount } from "svelte";
  import { page } from "$app/state";
  import { goto } from "$app/navigation";
  import { authStore } from "$lib/stores";
  import { listHistoryStore, groupLists, ListHistoryManager } from "$lib/stores/listHistoryStore";
  import { dotsHorizontalIcon } from "$lib/images/icons";
  import { SVG } from "$lib/components";
  import { Trash2, Users, Lock, UnlockIcon } from "lucide-svelte";
  import { DropdownMenu } from "$lib/components/ui";
  import { cn } from "$lib/utility";
  import { ListAPI } from "$lib/api/list";
  import { routes } from "$lib/routes";

  type $$Events = {
    listHistoryNavigation: CustomEvent<any>;
  };

  import { createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher();

  let containerRef = $state<HTMLDivElement>();
  let observerTarget = $state<HTMLDivElement>();
  let observer = $state<IntersectionObserver>();

  const BATCH_SIZE = 30;
  const PLACEHOLDER_LISTS = 5;

  let isLoading = $state(true);
  let isLoadingMore = $state(false);
  let hasMoreResults = $state(true);
  let currentOffset = $state(0);
  let isMounted = $state(false);
  let previousUserId = $state<string | null>(null);

  const FETCH_DELAY = 500;
  let lastFetchTime = $state(0);
  let isInitialLoad = $state(true);

  const listHistoryState = writable({
    isInitialized: false,
    lastKnownOffset: 0,
    hasMore: true,
  });

  const groupedListHistory = $derived(() => {
    const userID = ListHistoryManager.getUserIdentifier($authStore.user);
    const relevantLists = $listHistoryStore.filter((list) => {
      const isRelevant =
        String(list.userId) === String(userID) ||
        (String(list.userId) === ListHistoryManager.ANONYMOUS_USER && !$authStore.user);
      return isRelevant;
    });
    return groupLists(relevantLists);
  });

  $effect(() => {
    logger.log("groupedListHistory", groupedListHistory());
  });

  async function loadListHistory(offset: number = 0): Promise<void> {
    const userId = get(authStore).user?.id;
    if (!userId) {
      isLoading = false;
      return;
    }

    isLoading = true;
    listHistoryStore.setLists([]);

    try {
      logger.log("Loading lists...");
      const lists = await ListAPI.getUserLists();
      logger.log("lists", lists);

      if (lists && Array.isArray(lists)) {
        listHistoryStore.setLists(lists);

        currentOffset = lists.length;
        hasMoreResults = lists.length >= BATCH_SIZE;

        listHistoryState.set({
          isInitialized: true,
          lastKnownOffset: currentOffset,
          hasMore: hasMoreResults,
        });
      }
    } catch (err) {
      console.error("Error loading lists:", err);
    } finally {
      isLoading = false;
      isInitialLoad = false;
      checkViewportFill();
    }
  }

  $effect(() => {
    if ($authStore.user?.id && previousUserId !== $authStore.user?.id) {
      const userId = Number($authStore.user.id);
      if (!isNaN(userId)) {
        hasMoreResults = true;
        previousUserId = $authStore.user.id;
        loadListHistory(currentOffset);
      }
    }
  });

  function setupObserver() {
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreResults && !isLoadingMore) {
          loadMoreLists();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0,
      },
    );

    if (observerTarget) {
      observer.observe(observerTarget);
    }
  }

  async function handleDelete(id: number, slug: string) {
    try {
      await ListAPI.deleteList(id, slug);
      listHistoryStore.removeList(slug); // Store still uses slug

      if (page.params.slug === slug) {
        goto(routes.lists);
      }
    } catch (err) {
      console.error("Error deleting list:", err);
      loadListHistory(0);
    }
  }

  async function handleTogglePrivacy(slug: string, isPublic: boolean) {
    try {
      listHistoryStore.updateList(slug, { isPublic: !isPublic });
    } catch (err) {
      console.error("Error updating list privacy:", err);
    }
  }

  async function checkViewportFill() {
    if (!containerRef || !observerTarget || !hasMoreResults || isLoadingMore || !isMounted) return;

    const containerHeight = containerRef.clientHeight;
    const scrollHeight = containerRef.scrollHeight;
    const threshold = containerHeight * 1.2;

    if (scrollHeight < threshold && hasMoreResults) {
      await loadMoreLists();

      if (scrollHeight < threshold && hasMoreResults) {
        setTimeout(() => checkViewportFill(), 100);
      }
    } else {
      setupObserver();
    }
  }

  async function loadMoreLists(): Promise<void> {
    if (!get(authStore).user?.id || !hasMoreResults || isLoadingMore) return;

    const now = Date.now();
    if (now - lastFetchTime < FETCH_DELAY && !isInitialLoad) return;

    try {
      isLoadingMore = true;
      lastFetchTime = now;

      logger.log("Loading more lists...");
      const lists = await ListAPI.getUserLists();
      logger.log("lists", lists);

      if (lists && Array.isArray(lists)) {
        const newLists = lists.slice(currentOffset, currentOffset + BATCH_SIZE);
        if (newLists.length > 0) {
          listHistoryStore.appendLists(newLists);
          currentOffset += newLists.length;
          hasMoreResults = newLists.length >= BATCH_SIZE;
        } else {
          hasMoreResults = false;
        }
      }
    } catch (err) {
      console.error("Error loading more lists:", err);
      hasMoreResults = false;
    } finally {
      isLoadingMore = false;
    }
  }

  function navigateToList(slug: string) {
    goto(`/lists/${slug}`);
  }

  function handleResize() {
    checkViewportFill();
  }

  onMount(() => {
    isMounted = true;

    if (get(authStore).isLoggedIn) {
      window.addEventListener("resize", handleResize);
    } else {
      isLoading = false;
    }

    return () => {
      isMounted = false;
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("resize", handleResize);

      listHistoryState.set({
        isInitialized: true,
        lastKnownOffset: currentOffset,
        hasMore: hasMoreResults,
      });
    };
  });
</script>

<div bind:this={containerRef} class="custom-scrollbar flex-1 overflow-y-auto overflow-x-hidden">
  <div class="mt-1.5 flex flex-col gap-5 text-xs">
    {#each Object.entries(groupedListHistory()) as [group, lists]}
      {#if lists.length > 0}
        <div>
          <div class="mb-2 font-bold dark:text-theme-gray-700">{group}</div>
          <ul>
            {#each lists as list (list.slug)}
              <li class="rounded-base-base mb-3 flex items-center justify-between gap-1 font-light">
                <button
                  class={cn(
                    "flex-grow truncate text-left lg:max-w-[200px]",
                    "text-theme-text-primary dark:text-theme-gray-600",
                    "hover:font-bold dark:hover:text-theme-gray-800",
                    "mr-2 transition-all",
                    page.params.slug === list.slug ? "font-bold dark:text-theme-gray-800" : "transition-default",
                  )}
                  onclick={(event) => {
                    dispatch("listHistoryNavigation", {});
                    navigateToList(list.slug);
                  }}
                >
                  {list.title}
                </button>

                <div class="group/list-item flex items-center">
                  <span class="mr-2 flex items-center justify-between gap-2">
                    <span class="text-theme-text-primary dark:text-theme-gray-500">
                      <!-- {list.totalResults ?? 0} -->
                    </span>
                    <Users class="inline h-3.5 w-3.5 dark:text-theme-gray-500" strokeWidth={1.5} />
                  </span>

                  <!-- Three dots menu -->
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <button
                        class="opacity-0 transition-opacity duration-200 group-hover/list-item:opacity-100 dark:text-theme-gray-500 dark:hover:text-theme-gray-800"
                      >
                        <SVG className="text-theme-text-primary dark:text-theme-gray-500" file={dotsHorizontalIcon} />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content
                      side="top"
                      class="!rounded-base !p-0 shadow-sm dark:border dark:border-white/10 dark:bg-theme-background-tertiary"
                    >
                      <!-- Toggle Privacy -->
                      <DropdownMenu.Item
                        class={cn(
                          "hover:cursor-pointer",
                          "text-theme-text-primary dark:text-theme-gray-600 dark:hover:text-theme-gray-800",
                          "flex items-center gap-2 text-xs",
                          "dark:hover:bg-theme-background-primary",
                        )}
                        onclick={() => handleTogglePrivacy(list.slug, list.isPublic)}
                      >
                        {#if list.isPublic}
                          <Lock class="h-4 w-4" strokeWidth={1} />
                          <span>Make Private</span>
                        {:else}
                          <UnlockIcon class="h-4 w-4" strokeWidth={1} />
                          <span>Make Public</span>
                        {/if}
                      </DropdownMenu.Item>

                      <!-- Delete -->
                      <DropdownMenu.Item
                        class={cn(
                          "hover:cursor-pointer",
                          "text-theme-red dark:text-theme-gray-600 dark:hover:text-theme-red",
                          "flex items-center gap-2 text-xs",
                          "dark:hover:bg-theme-background-primary",
                        )}
                        onclick={() => handleDelete(list.id, list.slug)}
                      >
                        <Trash2 class="h-4 w-4" strokeWidth={1} />
                        <span>Delete</span>
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </div>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
    {/each}

    {#if !$authStore.isLoggedIn}
      <div class="text-theme-text-secondary dark:text-theme-gray-500">Log in to see your lists</div>
    {:else if Object.values(groupedListHistory()).flat().length === 0 && !isLoading && !isLoadingMore}
      <div class="text-theme-text-secondary dark:text-theme-gray-500">Your lists will appear here</div>
    {/if}

    <div class="invisible w-full">
      <div bind:this={observerTarget} class="h-0 w-full" aria-hidden="true"></div>
    </div>
  </div>

  {#if isLoadingMore}
    <div class="flex flex-col gap-6 pb-1.5 text-xs">
      {#each Array(1) as _, groupIndex}
        <div>
          <ul>
            {#each Array(PLACEHOLDER_LISTS) as _, itemIndex}
              <li class="mb-4 flex animate-pulse items-center justify-between">
                <div class="h-3.5 w-[75%] rounded-base bg-gray-100 dark:bg-theme-background-tertiary"></div>
                <div class="mr-6 h-3.5 w-6 rounded-base bg-gray-100 dark:bg-theme-background-tertiary"></div>
              </li>
            {/each}
          </ul>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: black transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: black;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background-color: transparent;
  }
</style>
